@import 'tailwindcss';
@import 'tw-animate-css';

@custom-variant dark (&:is(.dark *));

/* RealMathUniverse - Tactical Research Console Theme */
:root {
  /* Deep space background with subtle blue undertone */
  --background: oklch(0.08 0.02 260);
  --foreground: oklch(0.92 0.01 200);
  
  /* Card/Panel colors - slightly elevated from background */
  --card: oklch(0.12 0.02 260);
  --card-foreground: oklch(0.92 0.01 200);
  
  /* Popover/dropdown colors */
  --popover: oklch(0.10 0.02 260);
  --popover-foreground: oklch(0.92 0.01 200);
  
  /* Primary - Cyan/Teal accent for key elements */
  --primary: oklch(0.75 0.15 195);
  --primary-foreground: oklch(0.10 0.02 260);
  
  /* Secondary - Muted blue-gray */
  --secondary: oklch(0.25 0.03 260);
  --secondary-foreground: oklch(0.85 0.02 200);
  
  /* Muted elements */
  --muted: oklch(0.18 0.02 260);
  --muted-foreground: oklch(0.60 0.02 200);
  
  /* Accent - Warm amber for warnings/highlights */
  --accent: oklch(0.75 0.18 75);
  --accent-foreground: oklch(0.10 0.02 260);
  
  /* Destructive - Red for errors/alerts */
  --destructive: oklch(0.55 0.22 25);
  --destructive-foreground: oklch(0.95 0.02 25);
  
  /* Success - Green for positive states */
  --success: oklch(0.65 0.18 145);
  --success-foreground: oklch(0.10 0.02 145);
  
  /* Warning - Amber */
  --warning: oklch(0.75 0.18 75);
  --warning-foreground: oklch(0.10 0.02 75);
  
  /* Info - Blue */
  --info: oklch(0.65 0.15 240);
  --info-foreground: oklch(0.95 0.01 240);
  
  /* Border and input */
  --border: oklch(0.25 0.03 260);
  --input: oklch(0.15 0.02 260);
  --ring: oklch(0.75 0.15 195);
  
  /* Chart colors - Scientific visualization palette */
  --chart-1: oklch(0.70 0.20 195); /* Cyan - electrons */
  --chart-2: oklch(0.65 0.22 25);  /* Red - positrons */
  --chart-3: oklch(0.70 0.18 145); /* Green - quarks */
  --chart-4: oklch(0.75 0.20 300); /* Purple - neutrinos */
  --chart-5: oklch(0.80 0.18 75);  /* Amber - bosons */
  
  --radius: 0.375rem;
  
  /* HUD-specific colors */
  --hud-bg: oklch(0.06 0.02 260 / 0.92);
  --hud-border: oklch(0.35 0.08 195);
  --hud-text: oklch(0.85 0.05 195);
  --hud-text-dim: oklch(0.55 0.03 200);
  --hud-highlight: oklch(0.80 0.15 195);
  --hud-warning: oklch(0.80 0.20 75);
  --hud-error: oklch(0.70 0.25 25);
  --hud-success: oklch(0.70 0.18 145);
  
  /* Glow effects */
  --glow-primary: oklch(0.75 0.15 195 / 0.4);
  --glow-warning: oklch(0.75 0.18 75 / 0.4);
  --glow-error: oklch(0.55 0.22 25 / 0.4);
}

.dark {
  --background: oklch(0.06 0.02 260);
  --foreground: oklch(0.92 0.01 200);
  --card: oklch(0.10 0.02 260);
  --card-foreground: oklch(0.92 0.01 200);
  --popover: oklch(0.08 0.02 260);
  --popover-foreground: oklch(0.92 0.01 200);
  --primary: oklch(0.75 0.15 195);
  --primary-foreground: oklch(0.10 0.02 260);
  --secondary: oklch(0.22 0.03 260);
  --secondary-foreground: oklch(0.85 0.02 200);
  --muted: oklch(0.15 0.02 260);
  --muted-foreground: oklch(0.55 0.02 200);
  --accent: oklch(0.75 0.18 75);
  --accent-foreground: oklch(0.10 0.02 260);
  --destructive: oklch(0.55 0.22 25);
  --destructive-foreground: oklch(0.95 0.02 25);
  --border: oklch(0.22 0.03 260);
  --input: oklch(0.12 0.02 260);
  --ring: oklch(0.75 0.15 195);
}

@theme inline {
  --font-sans: 'Inter', 'Inter Fallback', system-ui, sans-serif;
  --font-mono: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-popover: var(--popover);
  --color-popover-foreground: var(--popover-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-destructive: var(--destructive);
  --color-destructive-foreground: var(--destructive-foreground);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-ring: var(--ring);
  --color-chart-1: var(--chart-1);
  --color-chart-2: var(--chart-2);
  --color-chart-3: var(--chart-3);
  --color-chart-4: var(--chart-4);
  --color-chart-5: var(--chart-5);
  --radius-sm: calc(var(--radius) - 2px);
  --radius-md: var(--radius);
  --radius-lg: calc(var(--radius) + 2px);
  --radius-xl: calc(var(--radius) + 6px);
}

@layer base {
  * {
    @apply border-border outline-ring/50;
  }
  body {
    @apply bg-background text-foreground;
  }
}

/* Custom scrollbar for HUD panels */
.hud-scrollbar::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.hud-scrollbar::-webkit-scrollbar-track {
  background: var(--muted);
  border-radius: 2px;
}

.hud-scrollbar::-webkit-scrollbar-thumb {
  background: var(--primary);
  border-radius: 2px;
}

.hud-scrollbar::-webkit-scrollbar-thumb:hover {
  background: var(--hud-highlight);
}

/* Glow effects for HUD elements */
.hud-glow {
  box-shadow: 0 0 10px var(--glow-primary), 0 0 20px var(--glow-primary);
}

.hud-glow-warning {
  box-shadow: 0 0 10px var(--glow-warning), 0 0 20px var(--glow-warning);
}

.hud-glow-error {
  box-shadow: 0 0 10px var(--glow-error), 0 0 20px var(--glow-error);
}

/* Scanline effect for retro terminal look */
.scanlines::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: repeating-linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.1) 0px,
    rgba(0, 0, 0, 0.1) 1px,
    transparent 1px,
    transparent 2px
  );
  pointer-events: none;
  z-index: 10;
}

/* Blinking cursor animation */
@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

.cursor-blink {
  animation: blink 1s infinite;
}

/* Pulse animation for alerts */
@keyframes pulse-glow {
  0%, 100% { 
    box-shadow: 0 0 5px var(--glow-primary);
    opacity: 1;
  }
  50% { 
    box-shadow: 0 0 20px var(--glow-primary), 0 0 30px var(--glow-primary);
    opacity: 0.8;
  }
}

.pulse-glow {
  animation: pulse-glow 2s ease-in-out infinite;
}

/* Data stream animation */
@keyframes data-stream {
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
}

.data-stream {
  animation: data-stream 3s linear infinite;
}

/* Number ticker effect */
.tabular-nums {
  font-variant-numeric: tabular-nums;
}

/* RMU website deployment aesthetic: Furuno / glass-bridge / tactical data-console inspired. */
.rmu-mfd-shell canvas {
  filter: saturate(1.18) contrast(1.08) brightness(0.92);
}

.rmu-screen-vignette {
  background:
    radial-gradient(circle at 50% 45%, transparent 0%, transparent 42%, rgba(0, 0, 0, 0.46) 100%),
    linear-gradient(rgba(122, 255, 91, 0.035) 50%, rgba(0, 0, 0, 0.035) 50%);
  background-size: 100% 100%, 100% 4px;
  mix-blend-mode: screen;
}

.rmu-radar-sweep {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 50%, rgba(122,255,91,0.18) 0 2px, transparent 3px),
    repeating-radial-gradient(circle at 50% 50%, transparent 0 13px, rgba(122,255,91,0.16) 14px, transparent 15px),
    linear-gradient(90deg, rgba(122,255,91,0.13), transparent 50%);
}

.rmu-radar-sweep::after {
  content: "";
  position: absolute;
  inset: -50%;
  background: conic-gradient(from 0deg, rgba(122,255,91,0.46), transparent 22deg, transparent 360deg);
  animation: rmuRadarSweep 3.8s linear infinite;
  transform-origin: center center;
}

@keyframes rmuRadarSweep {
  to { transform: rotate(360deg); }
}
